import React, { FC, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetCountryListForDropdowns } from "../../../../api/services/client/clientApis";
import { ReactComponent as DeleteIcon } from "../../../../assets/images/delete-icon.svg";
import {
  FormController,
  MuiBox,
  MuiButtonSecondary,
  MuiGrid,
  MuiIconButton,
  MuiSelect,
  MuiTable,
} from "../../../../components";
import MeasurementInput from "../../../../components/measurementInput/MeasurementInput";
import { gridSize3Column } from "../../../../helpers/constants";
import {
  commonSiteCoreKeys,
  UnitTypesKey,
} from "../../../../helpers/constants/siteCoreKeys";
import { DefaultValueExceptionType } from "../../../../helpers/types/common";
import {
  getCountryNameByCode,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { toggleToast } from "../../../../redux/slices/appState/appStateSlice";
import { selectedUnitSelector } from "../../../../redux/slices/mdmActivity/mdmActivitySelector";
import { AddTopicFormKey } from "../../modelsAndSchemas/models";
import { addTopicSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

interface MdmTopicExceptionsProps {
  pageId: string;
  setSelectedCountry: React.Dispatch<React.SetStateAction<string>>;
  addExceptionHandler: () => void;
  deleteAction: (countryCode: string) => void;
  rows: DefaultValueExceptionType[];
  isEditTopic?: boolean;
}

export const MdmTopicExceptions: FC<MdmTopicExceptionsProps> = ({
  pageId,
  setSelectedCountry,
  addExceptionHandler,
  deleteAction,
  rows,
  isEditTopic = false,
}) => {
  const {
    commonLabels,
    fetchLabel,
    commonValidationMessages,
    fetchValidationMessage,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { data: countryListData, isError } = useGetCountryListForDropdowns();
  const dispatch = useDispatch();
  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const selectedUnit = useSelector(selectedUnitSelector);

  const [filteredResults, setFilteredResults] = useState<
    DefaultValueExceptionType[]
  >([]);

  const { control, clearErrors, getValues, setError } = useFormContext();

  const [exceptionsItems, setExceptionsItems] = useState<string[]>([]);

  const {
    countryLabel,
    addTopic,
    minutesList,
    minutesLabel,
    quantityLabel,
    numberOfDaysLabel,
    actionHeader,
    noExceptionsAvailableMessage,
  } = addTopicSiteCoreKeys;

  const getAddTopicLabel = (key: string) => {
    return fetchLabel(addTopic, key);
  };

  useEffect(() => {
    const myData = structuredClone(rows);

    myData.forEach((data) => {
      data.country = getCountryNameByCode(
        data.country ?? "",
        countryListData ?? [],
      );
    });

    setFilteredResults([...myData]);
    setExceptionsItems([...new Set(myData.map((item) => item.countryCode))]);
  }, [rows, countryListData]);

  useEffect(() => {
    if (isError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isError]);

  const measurementOtherLabel =
    selectedUnit === UnitTypesKey.Count ? quantityLabel : numberOfDaysLabel;

  const measurementLabel =
    selectedUnit === UnitTypesKey.Minutes
      ? getAddTopicLabel(minutesLabel)
      : getAddTopicLabel(measurementOtherLabel);

  const columns = [
    {
      id: AddTopicFormKey.country,
      label: getAddTopicLabel(countryLabel),
      isSortVisible: true,
    },
    {
      id: AddTopicFormKey.measurementName,
      label: measurementLabel,
      isSortVisible: true,
    },
    {
      id: "action",
      label: getAddTopicLabel(actionHeader),
      render: (row: any) => {
        return (
          <MuiIconButton
            onClick={() => deleteAction(row.countryCode)}
            data-testid="delete-btn"
            aria-label={commonARIALabel.delete}
          >
            <DeleteIcon />
          </MuiIconButton>
        );
      },
    },
  ];

  const handleAddException = async () => {
    const unit = selectedUnit;
    const country = getValues(AddTopicFormKey.country);
    const measurementMinute = getValues(
      AddTopicFormKey.countryMeasurementMinute,
    );
    const measurementOther = getValues(AddTopicFormKey.countryMeasurementOther);

    let hasError = false;

    // Validate country manually
    if (!country) {
      setError(AddTopicFormKey.country, {
        type: "manual",
        message: fetchValidationMessage(commonSiteCoreKeys.countryRequired),
      });
      hasError = true;
    } else {
      clearErrors(AddTopicFormKey.country);
    }

    // Validate countryMeasurementMinute if unit is Minutes
    if (unit === UnitTypesKey.Minutes && !measurementMinute) {
      setError(AddTopicFormKey.countryMeasurementMinute, {
        type: "manual",
        message: fetchValidationMessage(commonSiteCoreKeys.minutesRequired),
      });
      hasError = true;
    } else {
      clearErrors(AddTopicFormKey.countryMeasurementMinute);
    }

    // Validate countryMeasurementOther if unit is Count or Days
    if (
      (unit === UnitTypesKey.Count || unit === UnitTypesKey.Days) &&
      (measurementOther === undefined ||
        measurementOther < 1 ||
        measurementOther > 99)
    ) {
      setError(AddTopicFormKey.countryMeasurementOther, {
        type: "manual",
        message:
          measurementOther === undefined ||
          measurementOther === 0 ||
          measurementOther === ""
            ? commonValidationMessages.requiredValidation
            : commonValidationMessages.valueRangeFieldValidator1To99,
      });
      hasError = true;
    } else {
      clearErrors(AddTopicFormKey.countryMeasurementOther);
    }

    if (hasError) return;

    addExceptionHandler();
  };

  return (
    <>
      <MuiGrid
        container
        className="form-container"
        data-testid="exceptions-container"
      >
        <MuiGrid size={gridSize3Column} className="">
          <FormController control={control} name={AddTopicFormKey.country}>
            <MuiSelect
              label={getAddTopicLabel(countryLabel)}
              options={setApiDataToDropDownOption(
                countryListData?.filter(
                  (item: any) => !exceptionsItems.includes(item.key), // filter out already selected countries
                ),
                true,
              )}
              value=""
              id={`${pageId}-${AddTopicFormKey.country}`}
              visibleDefaultSelectOption
              placeholder={commonLabels.selectPlaceHolder}
              onChange={(e) => setSelectedCountry(e.target.value as string)}
              requiredARIALabel={commonARIALabel.required}
              showSearch
              searchPlaceholder={commonLabels.search}
            />
          </FormController>
        </MuiGrid>
        {selectedUnit && selectedUnit !== UnitTypesKey.HalfDay && (
          <MuiGrid size={gridSize3Column} className="">
            <MeasurementInput
              selectedUnit={selectedUnit}
              measurementOtherMinuteName={
                AddTopicFormKey.countryMeasurementMinute
              }
              measurementOther={AddTopicFormKey.countryMeasurementOther}
              control={control}
              pageId={pageId}
              parentKey={addTopic}
              minutesLabelKey={minutesLabel}
              quantityLabelKey={quantityLabel}
              daysLabelKey={numberOfDaysLabel}
              minuteListKey={minutesList}
              isMandatory={false}
            />
          </MuiGrid>
        )}
        <MuiGrid className="add-button-container">
          <MuiButtonSecondary
            onClick={handleAddException}
            className="add-button"
            id="add-exception-btn"
          >
            {commonLabels.add}
          </MuiButtonSecondary>
        </MuiGrid>
      </MuiGrid>

      <MuiBox className="table-container">
        <MuiTable
          ref={tableRef}
          pageId={pageId}
          tableHeaderForm={[]}
          columns={columns}
          tableResults={[...filteredResults]}
          noDataInApi={rows.length === 0}
          noResultMessage={commonLabels.noResult}
          emptyMessage={getAddTopicLabel(noExceptionsAvailableMessage)}
          priorityResults={[]}
          // footerColSpan={selectedUnit === UnitTypesKey.HalfDay ? 1 : 2}
        />
      </MuiBox>
    </>
  );
};
